<template>
  <b-overlay :show="loading">
    <div class="pt-2 pb-2 pl-2 pr-2 border card shadow">
      <div class="pb-1" sty>
        Report - Patient
      </div>
      <b-row>
        <b-col md="6">
          <patient-filter-select 
            :value="patient"
            @change="val => patient = val"
          />
        </b-col>
      </b-row>
    </div>

    <div v-if="totalNumberOfConsultations > 0">
      <b-row>
       <b-col md="6">
          <div class="pt-2 pb-2 pl-2 pr-2 border card shadow">
            <div class="pb-2">
              Total Number of Consultations:<span> {{ totalNumberOfConsultations }} </span>
            </div>
        </div>
        </b-col>
      </b-row>
    </div>

    <div v-if="allLabsData.length > 0">
      <div>
        List of Labs {{ allLabsData.length > 0 ? `(${allLabsData.length})` : '' }}
      </div>
      <list-table
        :records="allLabsData" 
        :columns="LabTableColumns"
        :show-import-button="false"
        :show-search="false"
        :total-records="totalRecords"
        :show-export-button="can('patients_reports:CanExport')"
        :current-page-number.sync="currentPage"
        @export="onExportLabs"
      >
        <template #cell(lab_type)="data">
          <span 
            v-for="(labType, index) in getValueFromSource(data.item, 'lab_types', [])"
            :key="labType._id"
          >
            {{ labType.name }}
            <span v-if="index < data.item.lab_types.length - 1">, </span> 
          </span>
        </template>

        <template #cell(is_emergency)="data">
          <b-badge
            pill
            :variant="`light-${data.item.is_emergency ? 'danger': 'primary'}`"
            class="text-capitalize"
          >
            {{ data.item.is_emergency ? 'Yes' : 'No' }}
          </b-badge>
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="resolveStatusVariant(data.item.status)"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>
      </list-table>
    </div>

    <div v-if="allMedicationsData.length > 0">
      <div class="pt-1">
        List of Medications {{ allMedicationsData.length > 0 ? `(${allMedicationsData.length})` : '' }}
      </div>
      <list-table
        :records="allMedicationsData" 
        :columns="medicationsTableColumns"
        :show-import-button="false"
        :show-search="false"
        :total-records="totalRecords"
        :show-export-button="can('patients_reports:CanExport')"
        :current-page-number.sync="currentPage"
        @export="onExportMedications"
      >
        <template #cell(doctor_name)="data">
          <span> {{ getValueFromSource(data.item, 'doctor.first_name') }} {{ getValueFromSource(data.item, 'doctor.last_name') }} </span> 
        </template>

        <template #cell(regiment)="data">
          <p>
            <b-badge
              variant="info"
              class="text-capitalize"
            >
              Dose
            </b-badge>
            <span> {{ data.item.dose }} </span> 
          </p>
          <p>
            <b-badge
              variant="info"
              class="text-capitalize"
            >
              When
            </b-badge>
            <span> {{ getValueFromSource(data.item, 'drug.dose_frequency.name') }} </span> 
          </p>
          <p>
            <b-badge
              :variant="`info`"
              class="text-capitalize"
            >
              For
            </b-badge>
            <span> {{ `${data.item.duration} ${startCase(data.item.duration_unit)}` }} </span> 
          </p>
          <p>
            <b-badge
              :variant="`info`"
              class="text-capitalize"
            >
              How
            </b-badge>
            <span> {{ getValueFromSource(data.item, 'drug.route.name') }} </span> 
          </p>
        </template>

        <template #cell(status)="data">
          <b-badge
            v-if="getValueFromSource(data.item, 'prescription_delivery') && getValueFromSource(data.item, 'prescription_delivery.status')"
            pill
            :variant="resolveStatusVariant(getValueFromSource(data.item, 'prescription_delivery.status'))"
            class="text-capitalize"
          >
            {{ getUserStatusText(getValueFromSource(data.item, 'prescription_delivery.status')) }}
          </b-badge>
          <span v-else>-</span>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import PatientByAgeGroupChart from "@core/components/shared/admin/reports/view/AgeGroupChart.vue";
import PatientByGenderChart from "@core/components/shared/admin/reports/view/GenderChart.vue";
import PatientByRegionChart from "@core/components/shared/admin/reports/view/RegionChart.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import PatientFilterSelect from "@/@core/components/shared/admin/selects/PatientFilterSelect.vue";

import vSelect from 'vue-select';
import FileDownload from "js-file-download";

import { debounce, get, startCase } from "lodash";
import { BAlert, BAvatar, BBadge, BCard, BCardImg, BCol, BFormDatepicker, BImg, BLink, BOverlay, BRow } from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchAllLabs()
    this.debounceFetchAllMedications()
    this.debounceFetchConsultationData()
  },
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debounceFetchAllLabs()
    this.debounceFetchAllMedications()
    this.debounceFetchConsultationData()
  }, 
  immediate: false
}

export default {
  components: {
  BCardImg,
  BOverlay,
  BAlert,
  BAvatar,
  BLink,
  BImg,
  BBadge,
  BRow,
  BCol,
  BCard,
  vSelect,
  ListTable,
  BFormDatepicker,
  PatientByRegionChart,
  PatientByGenderChart,
  PatientByAgeGroupChart,
  PatientFilterSelect
},
data(){
  return {
    loading: false,
    
    debounceFetchAllLabs: null,
    debounceFetchAllMedications: null,
    debounceFetchConsultationData: null,
    
    totalNumberOfConsultations: 0,
    patient: "",

    allLabsData: [],
    allMedicationsData: [],

    LabTableColumns: [
      { key: 'lab_category.title', label: "Lab Category", sortable: false },
      { key: 'lab_type', label: "Lab Type", sortable: false },
      { key: 'is_emergency', label: "Emergency", sortable: false, },
      { key: 'doctor_note', label: "Doctor Note", sortable: false, formatter: (val) => startCase(val) || '-' },
      { key: 'lab_note', label: "Lab Note", sortable: false, formatter: (val) => startCase(val) || '-' },
      { key: 'status' },
      { key: 'created', label: 'Date', sortable: false, formatter: val => `${this.formatDate(val)}`, },
    ],

    medicationsTableColumns: [
      { key: 'drug.generic_name', label: 'Medication', sortable: false, },
      { key: 'regiment', label: 'Regiment', sortable: false, },
      { key: 'doctor_name', label: "Doctor", sortable: false },
      { key: 'doctor.phone', label: "Doctor Phone", sortable: false },
      { key: 'status', label: 'Status', sortable: false, formatter: (val) => val || '-' },
      { key: 'created', label: 'Date', sortable: false, formatter: val => `${this.formatDate(val)}`, },
    ],     
  }
},
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    }
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debounceFetchAllLabs()
        this.debounceFetchAllMedications()
        this.debounceFetchConsultationData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,

    patient: watchHandlerAndResetPageNumber,
  },
  created() {
    this.debounceFetchAllLabs = debounce(this.fetchAllLabs, 500);
    this.debounceFetchAllMedications = debounce(this.fetchAllMedications, 500);
    this.debounceFetchConsultationData = debounce(this.fetchNumberOfConsultations, 500);

    const {
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    this.currentPage = +page;
    this.perPage = +limit;  
  },
  methods: {
    async fetchNumberOfConsultations() {
      try {
        this.loading = true;

        const query = {
          patient_id: this.patient,
        }

        if (this.patient){
          const request = await this.useJwt().mainService.patientReports.fetchNumberOfConsultationsForPatient(query);
          const { data } = request.data
          this.totalNumberOfConsultations = data
        } else {
          this.totalNumberOfConsultations = 0
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchAllLabs() {
      try {
        this.loading = true;

        const query = {
          patient_id: this.patient,
          page: this.currentPage,
          limit: this.perPage,
        }

        this.$router.push({ query }).catch(() => {});

        if (this.patient){
          const request = await this.useJwt().mainService.patientReports.fetchLabsForPatient(query);
          const { data, pagination } = request.data;
          
          this.totalRecords = pagination.totalRecords
          this.perPage = pagination.limit;
          this.allLabsData = data
        } else {
          this.allLabsData = []
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchAllMedications() {
      try {
        this.loading = true;

        const query = {
          patient_id: this.patient,
          page: this.currentPage,
          limit: this.perPage,
        }

        this.$router.push({ query }).catch(() => {});

        if (this.patient){
          const request = await this.useJwt().mainService.patientReports.fetchMedicationsForPatient(query);
          const { data, pagination } = request.data;
  
          this.totalRecords = pagination.totalRecords
          this.perPage = pagination.limit;
          this.allMedicationsData = data;
        } else {
          this.allMedicationsData = [];
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExportLabs(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          patient_id: this.patient,
        }

        const response = await this.useJwt().mainService.patientReports.exportLabs(query);
        FileDownload(response.data, "patient_lab_report.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async onExportMedications(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          patient_id: this.patient,
        }

        const response = await this.useJwt().mainService.patientReports.exportMedications(query);
        FileDownload(response.data, "patient_medication_report.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    startCase
  }
}
</script>

